/**
 * **画布状态** —— 视口、端口测量、选中、连接线。我们自己的那份 store。
 *
 * ## 它在替换谁
 *
 * 原来这些状态住在 Vue Flow 的 store 里（`useVueFlow()` 的返回值），
 * 而全仓库通过 `FlowStoreApi` 这个**结构类型**用它（见 `graph/flowStore.ts`）。
 * 现在换成我们自己的对象 —— 好消息是：那个结构类型当初就是为这一刻写的，
 * 所以：
 *
 * ```text
 *   组合式函数（useAddNode / useGrouping / useMenus / useMiniMapGesture …）
 *        ↓ 只认 FlowStoreApi 的方法名
 *   这个 store          ← 换实现只动这里
 * ```
 *
 * ## 三条设计约束（都不是随便定的）
 *
 * **① 文档不在我们手里。** `nodes` / `edges` 是**宿主**的（单写者，
 * 见 `documentSync.ts`）。这里只读、只**提议**变更（`emitNodeChanges`），
 * 于是"两个窗口看同一份文档"这件事不靠约定，靠结构。
 *
 * **② 测量是**唯一**的本地状态。** 节点多大、端口在哪，只能从 DOM 量
 * （折行 / 变参行 / 隐藏行都会让它变）。量出来的东西存这里，
 * 并且**带容差比较**（否则"测量 → 写 → 重渲染 → 又测量"是死循环，
 * 见 `handleGeometry.ts` 的文件头）。
 *
 * **③ 选中是文档的一部分，不是本地状态。** 所以"清空选中"要走
 * `emitNodeChanges` 发 select 变更 —— 这样多窗口才一致
 * （原来的教训：就地改 `node.selected` 只改到当前窗口那一份）。
 *
 * ## ⚠️ 这个文件 import vue（所以它**不在** `./graph` 入口里）
 *
 * `graph/canvas/` 里的 `.ts` 分两种：
 *   · **纯函数**（`viewport` / `edgeRouting` / `handleGeometry` /
 *     `paneInteraction`）—— 零依赖，进 `./graph` 入口，`verify:dist` 验；
 *   · **带响应式的这一份** —— 要 ref，所以只能待在库的主入口那一侧。
 * `src/graph.ts` 里显式列了前者，没有列它。
 */
import { reactive, ref, shallowRef } from 'vue'
import { DEFAULT_VIEWPORT, centerOn, clampViewport, fitViewport, screenToFlow } from './viewport'
import type { ZoomLimits } from './viewport'
import { sameDimensions, sameSocketOffsets, sizeFromRect, socketOffsetsFrom } from './handleGeometry'
import type { Size } from './handleGeometry'
import type { Point } from '../../types/geometry'
import type { Viewport } from '../minimapGesture'
import type { FlowEdge, FlowNode } from '../flowTypes'
import type { FlowEdgeChange, FlowNodeChange } from '../flowChanges'

/** 量出来的几何事实（画布单位） */
export interface CanvasMeasure extends Size {
  /** 端口 id → 相对节点左上角的偏移 */
  sockets: Record<string, Point>
}

/** 一条正在拖的连线（从哪个端口出发、指针现在在哪） */
export interface CanvasConnection {
  nodeId: string
  handleId: string
  type: 'source' | 'target'
  /** 端口在节点的哪一侧（决定预览线从左边还是右边出来） */
  side: 'left' | 'right'
  /** 指针当前位置（画布坐标）；还没动过时为空 */
  pointer: Point | null
}

export interface CanvasStoreOptions extends ZoomLimits {
  /** 画布根元素（量尺寸、换算坐标都要它） */
  root: () => HTMLElement | null
  nodes: () => readonly FlowNode[]
  edges: () => readonly FlowEdge[]
  /** 提议变更（宿主是唯一写者） */
  emitNodeChanges: (changes: FlowNodeChange[]) => void
  emitEdgeChanges: (changes: FlowEdgeChange[]) => void
  /** 视口变化（宿主用它记住"上次看到哪儿"） */
  emitViewportChange: (viewport: Viewport) => void
}

export function createCanvasStore(options: CanvasStoreOptions) {
  const viewport = ref<Viewport>({ ...DEFAULT_VIEWPORT })
  const measures = reactive(new Map<string, CanvasMeasure>())
  const connection = shallowRef<CanvasConnection | null>(null)
  const selectionActive = ref(false)

  /* ── 视口 ───────────────────────────────────────────────── */

  function hostSize(): Size {
    const rect = options.root()?.getBoundingClientRect()
    return { width: rect?.width ?? 0, height: rect?.height ?? 0 }
  }

  /** 视口中心（屏幕坐标，相对容器）—— `zoomIn/zoomOut` 绕它缩 */
  function hostCenter(): Point {
    const size = hostSize()
    return { x: size.width / 2, y: size.height / 2 }
  }

  function getViewport(): Viewport {
    return viewport.value
  }

  function setViewport(next: Viewport): void {
    const clamped = clampViewport(next, options)
    const current = viewport.value
    if (
      clamped.x === current.x &&
      clamped.y === current.y &&
      clamped.zoom === current.zoom
    ) {
      return
    }
    viewport.value = clamped
    options.emitViewportChange(clamped)
  }

  /**
   * 适配内容（`fitView`）。
   *
   * ⚠️ **调用方的 `minZoom` / `maxZoom` 优先于 props**。
   * 顺序写反了就出事：宿主的 `focusNode` 传 `maxZoom: 1.1`（"定位到新节点，
   * 最多放大到 1.1 倍"），被 props 的 `maxZoom: 3` 覆盖之后就会**放大过头**；
   * 而 `minZoom` 被覆盖时更隐蔽 —— 传进来的 0.5 会被替成 0.2。
   */
  function fitView(
    opts: { padding?: number; nodes?: { id: string }[]; minZoom?: number; maxZoom?: number } = {},
  ): void {
    const all = options.nodes()
    const subset = opts.nodes?.length
      ? all.filter((node) => opts.nodes!.some((n) => n.id === node.id))
      : all
    const boxes = subset.map((node) => ({
      x: node.position.x,
      y: node.position.y,
      width: measures.get(node.id)?.width ?? 0,
      height: measures.get(node.id)?.height ?? 0,
    }))
    setViewport(
      fitViewport(boundsOf(boxes), hostSize(), {
        ...options,
        padding: opts.padding,
        minZoom: opts.minZoom ?? options.minZoom,
        maxZoom: opts.maxZoom ?? options.maxZoom,
      }),
    )
  }

  function setCenter(x: number, y: number, opts: { zoom?: number } = {}): void {
    setViewport(
      centerOn({ x, y }, hostSize(), opts.zoom ?? viewport.value.zoom, options),
    )
  }

  function zoomIn(): void {
    zoomAroundHost(1.2)
  }

  function zoomOut(): void {
    zoomAroundHost(1 / 1.2)
  }

  function zoomAroundHost(factor: number): void {
    /* ⚠️ 绕容器中心缩放（原来调 `zoomIn({ duration })` 的那两处不带锚点） */
    const anchor = hostCenter()
    const zoom = clampViewport({ ...viewport.value, zoom: viewport.value.zoom * factor }, options).zoom
    if (zoom === viewport.value.zoom) return
    const flowAnchor = screenToFlow(viewport.value, anchor)
    const applied = zoom / viewport.value.zoom
    setViewport({
      x: viewport.value.x + flowAnchor.x * viewport.value.zoom * (1 - applied),
      y: viewport.value.y + flowAnchor.y * viewport.value.zoom * (1 - applied),
      zoom,
    })
  }

  function screenToFlowCoordinate(point: Point): Point {
    const rect = options.root()?.getBoundingClientRect()
    const local = rect ? { x: point.x - rect.left, y: point.y - rect.top } : point
    return screenToFlow(viewport.value, local)
  }

  /* ── 端口测量（`updateNodeInternals` 的替代）─────────────── */

  /**
   * **视野里实际的缩放** —— 从 DOM 读，而不是从 `viewport.value` 读。
   *
   * ## ⚠️ 为什么不能信 `viewport.value.zoom`（真机 bug 的根因）
   *
   * `setViewport(...)` 改的是 ref，DOM 上的 `transform` 要**下一帧**才更新。
   * 在这个窗口期内测量就会算错：
   *
   * ```text
   *   DOM: scale(1)   ref.zoom = 2   → 量到的屏幕尺寸 ÷ 2 = **一半**
   *   DOM: scale(2)   ref.zoom = 1   → 量到的屏幕尺寸 ÷ 1 = **两倍**
   * ```
   *
   * 存进去的是"画布单位"，所以错一倍就是**永久**错一倍（除非有人再量一次），
   * 而症状分散得看不出是一件事：
   *
   * ```text
   *   连线不在端口上，而在很远的地方（端口偏移错倍）
   *   小地图里的节点变成一个个小方块（尺寸错倍，缩到看不见）
   * ```
   *
   * 这个窗口期**真的会发生**：宿主在 `onInit` 的同一个 tick 里
   * `setViewport(记住的视口)`，紧接着就是挂载后那次测量 —— 恢复过视口的
   * 窗口从此一直是错的，没恢复过的（zoom=1）反而正常。这也解释了
   * 「左边窗口正常、右边窗口不对」这种"一个窗口好一个坏"的现象。
   *
   * 所以这里改成**读 DOM 上真正的矩阵**：`matrix(a, …)` 的 `a` 就是 scale。
   * 于是"什么时候量"不再影响结果 —— 这才是测量该有的性质。
   *
   * ⚠️ 读不到时（测试壳 / 老浏览器 / `transform: none`）回落到 ref 的值：
   * 那是"至少不会更糟"的选择，而不是假装读到了。
   */
  function domScale(root: HTMLElement): number {
    const layer = root.querySelector<HTMLElement>('.np-graph__viewport')
    if (!layer) return viewport.value.zoom
    /*
     * ⚠️ 先读 **inline style**（那是 Vue 真正渲染上去的那一份），
     * 读不到再退到 `getComputedStyle`。
     *
     * 顺序不能反：`getComputedStyle` 在测试壳（happy-dom）里没有布局引擎，
     * 返回的是空串 / `none` —— 反过来写的话，这条路径在套件里永远走不到，
     * 而它恰恰是修 bug 的那条路（"改了 ref 但还没渲染"的窗口期只有
     * inline style 说得清）。
     */
    const inline = layer.style?.transform ?? ''
    const computed =
      inline || typeof getComputedStyle !== 'function'
        ? ''
        : (getComputedStyle(layer)?.transform ?? '')
    const text = inline || computed
    const scale = parseScale(text)
    return Number.isFinite(scale) && scale > 0 ? scale : viewport.value.zoom
  }

  /** 从 `translate(...) scale(z)` 或 `matrix(a, …)` 里取缩放 */
  function parseScale(transform: string): number {
    if (!transform) return Number.NaN
    const scaleFn = /scale\(\s*([-\d.]+)\s*\)/.exec(transform)?.[1]
    if (scaleFn !== undefined) return Number(scaleFn)
    const matrix = /matrix\(\s*([-\d.]+)\s*,/.exec(transform)?.[1]
    return matrix !== undefined ? Number(matrix) : Number.NaN
  }

  /**
   * 重新量一批节点（不给 id 就是全量）。
   *
   * ⚠️ 一次 `querySelectorAll` 扫完，而不是每个 id 写一个选择器：
   * 节点 id 来自文档（可能有引号、空格、中文），拼进选择器会出各种怪事。
   */
  function updateNodeInternals(nodeIds?: readonly string[]): void {
    const root = options.root()
    if (!root) return
    const wanted = nodeIds?.length ? new Set(nodeIds) : null
    const zoom = domScale(root)
    const nodes = root.querySelectorAll<HTMLElement>('[data-id]')
    for (const element of nodes) {
      if (!element.classList.contains('np-graph__node')) continue
      const id = element.dataset.id
      if (!id) continue
      if (wanted && !wanted.has(id)) continue

      const nodeRect = element.getBoundingClientRect()
      if (nodeRect.width === 0 && nodeRect.height === 0) continue

      const socketRects: Record<string, { left: number; top: number; width: number; height: number }> = {}
      for (const handle of element.querySelectorAll<HTMLElement>('.np-graph__handle')) {
        const handleId = handle.dataset.handleid
        if (!handleId) continue
        socketRects[handleId] = handle.getBoundingClientRect()
      }

      const next: CanvasMeasure = {
        ...sizeFromRect(nodeRect, zoom),
        sockets: socketOffsetsFrom(socketRects, nodeRect, zoom),
      }
      const prev = measures.get(id)
      if (prev && sameMeasure(prev, next)) continue
      measures.set(id, next)
    }
  }

  function dimensionsOf(id: string): Size | undefined {
    const found = measures.get(id)
    return found ? { width: found.width, height: found.height } : undefined
  }

  function socketsOf(id: string): Record<string, Point> | undefined {
    return measures.get(id)?.sockets
  }

  /* ── 选中（**发变更**，不是就地改）───────────────────────── */

  function addSelectedNodes(list: readonly { id: string }[]): void {
    const wanted = new Set(list.map((item) => item.id))
    const changes: FlowNodeChange[] = []
    for (const node of options.nodes()) {
      if (wanted.has(node.id) && !node.selected) {
        changes.push({ id: node.id, type: 'select', selected: true })
      }
    }
    if (changes.length) options.emitNodeChanges(changes)
  }

  /**
   * 取消选中。
   *
   * ⚠️ 名字是 Vue Flow 的（它叫 `removeSelectedElements`，容易读成"删掉"），
   * 但**语义是"清空选中"** —— 实测过它的实现：
   * 不传参数时它只发 `select: false`，不删任何东西。宿主那边
   * （`useSubgraphLevels.clearSelection`）依赖的正是这个语义。
   */
  function removeSelectedElements(elements?: readonly { id: string }[]): void {
    const targets = elements?.length
      ? elements.map((item) => item.id)
      : [
          ...options.nodes().filter((node) => node.selected).map((node) => node.id),
          ...options.edges().filter((edge) => edge.selected).map((edge) => edge.id),
        ]
    if (targets.length === 0) return
    const wanted = new Set(targets)
    const nodeChanges: FlowNodeChange[] = options
      .nodes()
      .filter((node) => wanted.has(node.id) && node.selected)
      .map((node) => ({ id: node.id, type: 'select', selected: false }))
    const edgeChanges: FlowEdgeChange[] = options
      .edges()
      .filter((edge) => wanted.has(edge.id) && edge.selected)
      .map((edge) => ({ id: edge.id, type: 'select', selected: false }))
    if (nodeChanges.length) options.emitNodeChanges(nodeChanges)
    if (edgeChanges.length) options.emitEdgeChanges(edgeChanges)
  }

  function findNode(id: string): FlowNode | undefined {
    return options.nodes().find((node) => node.id === id)
  }

  /* ── 连线手势给画布的那点信息 ────────────────────────────── */

  /**
   * 把"正在拖的连线"指向某个端口。
   *
   * ⚠️ 宿主的 `repointConnectionLine` 会调它：从**已连线的输入**往外拖时，
   * 端点方向会变（输入 → 输出），预览线要从另一侧出来。
   */
  function startConnection(handle: {
    nodeId: string
    id: string
    type: 'source' | 'target'
    position?: string
  }): void {
    const side = handle.position === 'left' ? 'left' : handle.position === 'right' ? 'right' : undefined
    connection.value = {
      nodeId: handle.nodeId,
      handleId: handle.id,
      type: handle.type,
      side: side ?? (connection.value?.side ?? 'right'),
      pointer: connection.value?.pointer ?? null,
    }
  }

  function endConnection(): void {
    if (connection.value) connection.value = null
  }

  const store = {
    /* 文档（只读） */
    getNodes: () => options.nodes() as FlowNode[],
    getEdges: () => options.edges() as FlowEdge[],
    getSelectedNodes: () => options.nodes().filter((node) => node.selected) as FlowNode[],
    getSelectedEdges: () => options.edges().filter((edge) => edge.selected) as FlowEdge[],
    findNode,
    /* 视口 */
    getViewport,
    setViewport,
    fitView,
    setCenter,
    zoomIn,
    zoomOut,
    screenToFlowCoordinate,
    /* 测量 */
    updateNodeInternals,
    dimensionsOf,
    socketsOf,
    /* 选中 */
    addSelectedNodes,
    removeSelectedElements,
    /* 连线 */
    startConnection,
    endConnection,
    /* 我们自己加的（宿主可直接用） */
    viewport,
    measures,
    connection,
    /** 框选进行中（宿主在第二根手指落下时会写 `false` 来取消，见 `useCanvasPinch`） */
    userSelectionActive: selectionActive,
  }

  return { viewport, measures, connection, selectionActive, store }
}

export type CanvasStore = ReturnType<typeof createCanvasStore>['store']

/** 一组盒子的并集边界（内部小工具，避免为它多引一个模块） */
function boundsOf(
  boxes: readonly { x: number; y: number; width: number; height: number }[],
): { x: number; y: number; width: number; height: number } | null {
  if (boxes.length === 0) return null
  const left = Math.min(...boxes.map((b) => b.x))
  const top = Math.min(...boxes.map((b) => b.y))
  const right = Math.max(...boxes.map((b) => b.x + b.width))
  const bottom = Math.max(...boxes.map((b) => b.y + b.height))
  return { x: left, y: top, width: right - left, height: bottom - top }
}

/**
 * 测量结果是否"没变"。
 *
 * ⚠️ 复用 `handleGeometry` 那两个**被测过**的比较函数（带容差）——
 * 在这里再写一遍"差不多相等"是自找的漂移源，而它的后果是死循环。
 */
function sameMeasure(a: CanvasMeasure, b: CanvasMeasure): boolean {
  return sameDimensions(a, b) && sameSocketOffsets(a.sockets, b.sockets)
}
